import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import { useSnackbar } from "src/components/snackbar";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

export default function AgregarPortafolioTarea({
  open,
  onClose,
  tarea,
  portafolioSeleccionado,
}) {
  const classes = useStyles();
  const { empleado: user } = useSelector((state) => state.auth.user);
  const [descripcion, setDescripcion] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const [agregarPortafolio, { isLoading: isAdding }] =
    tareasApi.useAgregarPortafolioTareaMutation();
  const [actualizarPortafolio, { isLoading: isUpdating }] =
    tareasApi.useActualizarPortafolioTareaMutation();

  const isEditing = !!portafolioSeleccionado;
  const isLoading = isAdding || isUpdating;

  useEffect(() => {
    if (portafolioSeleccionado) {
      setDescripcion(portafolioSeleccionado.descripcion || "");
    } else {
      setDescripcion("");
    }
  }, [portafolioSeleccionado, open]);

  const handleClose = () => {
    if (!isLoading) {
      setDescripcion("");
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!descripcion.trim()) {
      enqueueSnackbar("La descripción es requerida", {
        variant: "info",
      });
      return;
    }

    try {
      const empleadoNombre = `${user.nombre} ${user.apellidos}`;

      if (isEditing) {
        await actualizarPortafolio({
          id: portafolioSeleccionado.id,
          descripcion: descripcion.trim(),
          empleadoIngresaPortafolio: empleadoNombre,
        }).unwrap();

        enqueueSnackbar("¡Entrada actualizada correctamente!", {
          variant: "success",
        });
      } else {
        await agregarPortafolio({
          refTarea: tarea.id,
          descripcion: descripcion.trim(),
          empleadoIngresaPortafolio: empleadoNombre,
        }).unwrap();
        enqueueSnackbar("¡Entrada creada correctamente!", {
          variant: "success",
        });
      }

      setDescripcion("");
      handleClose();
    } catch (error) {
      console.error("Error al guardar portafolio:", error);
      enqueueSnackbar(
        error.data?.message ||
          `Error al ${
            isEditing ? "actualizar" : "crear"
          } la entrada. Intenta nuevamente.`,
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableBackdropClick={isLoading}
        disableEscapeKeyDown={isLoading}
      >
        <DialogTitle>
          {isEditing ? "Editar Entrada" : "Nueva Entrada de Bitácora"}
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" className={classes.infoText}>
            {isEditing
              ? "Modifica la descripción de esta entrada"
              : "Registra las actividades y avances de esta tarea"}
          </Typography>

          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            label="Descripción"
            placeholder="Describe las actividades realizadas, avances, observaciones..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            disabled={isLoading}
            className={classes.textField}
            required
            helperText={`${descripcion.length} caracteres`}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading || !descripcion.trim()}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading
              ? "Guardando..."
              : isEditing
                ? "Actualizar"
                : "Crear Entrada"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
